/**
 * Horizon variant gallery — swaps product media to custom.variant_gallery metafield images.
 *
 * Listens to Shopify's native shopify:product:select event (Horizon variant picker).
 * Runs after Horizon replaces the gallery, then swaps metafield images without flicker.
 * Falls back to the default Shopify gallery when the metafield is empty.
 */
(function () {
  'use strict';

  const PRODUCT_SELECT_EVENT = 'shopify:product:select';
  const IMAGE_WIDTHS = [352, 832, 1200, 1600, 1920, 2560, 3840];
  const THUMB_WIDTH = 144;

  /** @type {boolean} */
  let bootstrapped = false;

  /** @type {boolean} */
  let isSwapping = false;

  /** Ignore MutationObserver while we rebuild / for a short settle window */
  let ignoreMutationsUntil = 0;

  /** @type {Map<string, VariantGallerySection>} */
  const sections = new Map();

  /**
   * @typedef {Object} GalleryImage
   * @property {string} src
   * @property {string} [alt]
   * @property {number} [width]
   * @property {number} [height]
   *
   * @typedef {Object} GalleryConfig
   * @property {string} sectionId
   * @property {string} productId
   * @property {number} initialVariantId
   * @property {string} colorOptionName
   * @property {number} colorOptionPosition
   */

  /**
   * Build a Shopify CDN image URL with width parameter.
   * @param {string} src
   * @param {number} width
   */
  function imageUrl(src, width) {
    try {
      const absolute = src.startsWith('//') ? `https:${src}` : src;
      const url = new URL(absolute, window.location.origin);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch {
      return src;
    }
  }

  /**
   * @param {string} src
   * @param {number[]} [widths]
   */
  function buildSrcset(src, widths = IMAGE_WIDTHS) {
    return widths.map((w) => `${imageUrl(src, w)} ${w}w`).join(', ');
  }

  /**
   * Wait for Horizon's media-gallery morph/replace to finish.
   */
  function waitForGalleryPaint() {
    return new Promise((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(resolve));
    });
  }

  /**
   * Find the PDP media-gallery inside the custom pdp-media wrapper or section.
   * @param {ParentNode} root
   * @returns {Element | null}
   */
  function findMediaGallery(root) {
    const mediaRoot = root.querySelector('[data-variant-gallery-media]');
    const scope = mediaRoot || root;
    return scope.querySelector('media-gallery');
  }

  /**
   * Wait until Horizon finishes replacing media-gallery after variant change.
   * @param {ParentNode} root
   * @param {Element | null | undefined} previousGallery
   * @param {number} [maxWaitMs]
   */
  async function waitForHorizonGalleryUpdate(root, previousGallery, maxWaitMs = 600) {
    await waitForGalleryPaint();

    return new Promise((resolve) => {
      const deadline = Date.now() + maxWaitMs;

      const finish = () => resolve(findMediaGallery(root));

      const observer = new MutationObserver(() => {
        const gallery = findMediaGallery(root);
        if (gallery && gallery !== previousGallery) {
          observer.disconnect();
          waitForGalleryPaint().then(finish);
        }
      });

      const mediaRoot = root.querySelector('[data-variant-gallery-media]') || root;
      observer.observe(mediaRoot, { childList: true, subtree: true });

      const poll = () => {
        const gallery = findMediaGallery(root);
        if (gallery && gallery !== previousGallery) {
          observer.disconnect();
          waitForGalleryPaint().then(finish);
          return;
        }
        if (Date.now() > deadline) {
          observer.disconnect();
          finish();
          return;
        }
        requestAnimationFrame(poll);
      };

      poll();
    });
  }

  /**
   * @param {number} ms
   */
  function delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Find Horizon's variant picker element in the section.
   * @param {ParentNode} root
   */
  function findVariantPicker(root) {
    const scope = root instanceof Document ? root : root;
    return (
      scope.querySelector?.('variant-main-picker') ||
      scope.querySelector?.('variant-picker') ||
      scope.querySelector?.('swatches-variant-picker-component') ||
      document.querySelector('variant-main-picker') ||
      document.querySelector('variant-picker')
    );
  }

  /**
   * @param {ParentNode} root
   * @returns {string | null}
   */
  function getCurrentVariantId(root) {
    const scope = root instanceof Document ? root : root;
    const formVariant =
      scope.querySelector?.('product-form-component input[name="id"]') ||
      scope.querySelector?.('form[action*="/cart/add"] input[name="id"]') ||
      document.querySelector('product-form-component input[name="id"]') ||
      document.querySelector('form[action*="/cart/add"] input[name="id"]');

    if (formVariant instanceof HTMLInputElement && formVariant.value) {
      return formVariant.value;
    }

    return getVariantIdFromUrl();
  }

  /**
   * @param {Record<string, unknown> | null | undefined} resource
   * @param {GalleryConfig} config
   * @param {ParentNode} [root]
   */
  function getColorValue(resource, config, root = document) {
    const colorNames = [config.colorOptionName || 'Color', 'Color', 'Colour'].map((n) =>
      n.toLowerCase().trim()
    );

    const picker = findVariantPicker(root);

    if (picker) {
      for (const fieldset of picker.querySelectorAll('fieldset')) {
        const legend = fieldset.querySelector('legend')?.textContent?.trim().toLowerCase() || '';
        const optionName = fieldset.dataset?.optionName?.toLowerCase() || '';
        const label = optionName || legend.replace(/^(color|colour)\s*/i, '').trim() || legend;

        if (colorNames.some((name) => legend === name || legend.startsWith(`${name} `) || legend.includes(name) || optionName === name || optionName.includes(name))) {
          const checked = fieldset.querySelector('input:checked');
          if (checked instanceof HTMLInputElement && checked.value) {
            return checked.value.trim();
          }
        }
      }

      for (const option of picker.querySelectorAll('[data-option-name]')) {
        const optionName = option.dataset?.optionName?.toLowerCase() || '';
        if (!colorNames.some((name) => optionName === name || optionName.includes(name))) continue;
        const checked = option.querySelector('input:checked, [aria-checked="true"], [aria-selected="true"]');
        if (checked instanceof HTMLInputElement && checked.value) return checked.value.trim();
        const dataValue = checked instanceof HTMLElement ? checked.dataset?.optionValue || checked.dataset?.value : null;
        if (dataValue) return String(dataValue).trim();
      }

      for (const select of picker.querySelectorAll('select')) {
        const selectName =
          select.dataset?.optionName?.toLowerCase() ||
          select.getAttribute('name')?.toLowerCase() ||
          '';
        if (colorNames.some((name) => selectName.includes(name))) {
          const selected = select.selectedOptions[0];
          if (selected?.value) return selected.value.trim();
        }
      }

      const checked = picker.querySelectorAll('fieldset input:checked, select option[selected]');
      for (const input of checked) {
        const optionName = input.dataset?.optionName?.toLowerCase();
        if (optionName && colorNames.includes(optionName)) {
          return (input.value || input.dataset?.value || '').trim() || null;
        }
      }
    }

    const options = resource?.options;
    if (Array.isArray(options)) {
      for (const option of options) {
        if (option && typeof option === 'object' && 'name' in option && 'value' in option) {
          const name = String(option.name || '')
            .toLowerCase()
            .trim();
          if (colorNames.includes(name)) {
            return String(option.value).trim();
          }
        }
      }
    }

    const position = config.colorOptionPosition;
    if (position === 1 && resource?.option1) return String(resource.option1).trim();
    if (position === 2 && resource?.option2) return String(resource.option2).trim();
    if (position === 3 && resource?.option3) return String(resource.option3).trim();

    return null;
  }

  /**
   * @param {string | number | null | undefined} variantId
   * @param {Record<string, unknown> | null | undefined} resource
   * @returns {GalleryImage[] | null}
   */
  function resolveGalleryImages(
    variantId,
    resource,
    config = window.variantGalleryConfig || {},
    root = document
  ) {
    const gallery = window.variantGallery || {};
    const byColor = window.variantGalleryByColor || {};
    const color = getColorValue(resource, config, root);

    console.log('[variant-gallery] Selected Variant:', variantId);
    console.log('[variant-gallery] Selected Color:', color);
    console.log('[variant-gallery] Variant resource:', resource);

    if (color) {
      const colorImages = resolveGalleryImagesByColor(color, config);
      if (Array.isArray(colorImages) && colorImages.length) {
        console.log('[variant-gallery] Images Found (color):', colorImages.length, colorImages);
        return colorImages;
      }
    }

    if (variantId != null) {
      const direct = gallery[String(variantId)] || gallery[Number(variantId)];
      if (Array.isArray(direct) && direct.length) {
        console.log('[variant-gallery] Images Found (variant id):', direct.length, direct);
        return direct;
      }
    }

    console.log('[variant-gallery] Images Found:', 0);
    return null;
  }

  /**
   * @param {GalleryImage[]} images
   */
  function gallerySignature(images) {
    return images.map((image) => image.src).join('|');
  }

  /**
   * @param {HTMLImageElement} img
   * @param {GalleryImage} image
   * @param {boolean} isPrimary
   */
  function applyImageToElement(img, image, isPrimary) {
    const highRes = imageUrl(image.src, 3840);
    img.src = imageUrl(image.src, isPrimary ? 1200 : 832);
    img.srcset = buildSrcset(image.src);
    img.sizes = img.getAttribute('sizes') || '(min-width: 750px) 50vw, 100vw';
    img.alt = image.alt || '';
    img.loading = isPrimary ? 'eager' : 'lazy';
    img.setAttribute('data_max_resolution', highRes);
    if (image.width) img.width = image.width;
    if (image.height) img.height = image.height;

    if (image.width && image.height) {
      const ratio = image.width / image.height;
      const container = img.closest('.product-media-container, slideshow-slide');
      if (container instanceof HTMLElement) {
        container.style.setProperty('--media-preview-ratio', String(ratio));
      }
    }
  }

  /**
   * @returns {boolean}
   */
  function hasGalleryData() {
    const gallery = window.variantGallery || {};
    const byColor = window.variantGalleryByColor || {};
    return Object.keys(gallery).length > 0 || Object.keys(byColor).length > 0;
  }

  /**
   * @param {GalleryImage[]} images
   */
  function preloadGalleryImages(images) {
    if (!images?.length) return Promise.resolve();

    return Promise.all(
      images.map(
        (image) =>
          new Promise((resolve) => {
            const loader = new Image();
            loader.decoding = 'async';
            const done = () => resolve();
            loader.onload = done;
            loader.onerror = done;
            loader.src = imageUrl(image.src, 1200);
          })
      )
    );
  }

  /**
   * @param {HTMLElement | null | undefined} fieldset
   * @param {string[]} colorNames
   */
  function isColorFieldset(fieldset, colorNames) {
    if (!(fieldset instanceof HTMLElement)) return false;
    const legend = fieldset.querySelector('legend')?.textContent?.trim().toLowerCase() || '';
    const optionName = fieldset.dataset?.optionName?.toLowerCase() || '';
    return colorNames.some(
      (name) =>
        legend === name ||
        legend.startsWith(`${name} `) ||
        legend.includes(name) ||
        optionName === name ||
        optionName.includes(name)
    );
  }

  /**
   * Read the color value from the swatch the shopper is clicking (before input:checked updates).
   * @param {Event} event
   * @param {GalleryConfig} config
   * @param {ParentNode} root
   */
  function getColorFromPickerInteraction(event, config, root) {
    if (!(event.target instanceof Element)) return null;

    const colorNames = [config.colorOptionName || 'Color', 'Color', 'Colour'].map((name) =>
      name.toLowerCase().trim()
    );

    const label = event.target.closest('label');
    if (label) {
      const input =
        label.control ||
        (label.htmlFor
          ? root.querySelector(`#${CSS.escape(label.htmlFor)}`)
          : label.querySelector('input[type="radio"], input[type="checkbox"]'));
      if (input instanceof HTMLInputElement && input.value) {
        const fieldset = input.closest('fieldset');
        if (isColorFieldset(fieldset, colorNames)) return input.value.trim();
      }
    }

    const input = event.target.closest('input[type="radio"], input[type="checkbox"]');
    if (input instanceof HTMLInputElement && input.value) {
      const fieldset = input.closest('fieldset');
      if (isColorFieldset(fieldset, colorNames)) return input.value.trim();
    }

    const optionValue = event.target.closest('[data-option-value]');
    if (optionValue instanceof HTMLElement) {
      const value = optionValue.dataset.optionValue || optionValue.getAttribute('data-value');
      if (value) return value.trim();
    }

    const picker = findVariantPicker(root);
    const colorOption = event.target.closest('[data-option-name]');
    if (picker && colorOption instanceof HTMLElement) {
      const optionName = colorOption.dataset?.optionName?.toLowerCase() || '';
      if (colorNames.some((name) => optionName === name || optionName.includes(name))) {
        const valueNode = event.target.closest('[data-option-value]');
        const value = valueNode instanceof HTMLElement
          ? valueNode.dataset.optionValue || valueNode.getAttribute('data-value')
          : null;
        if (value) return value.trim();
      }
    }

    return getColorValue(null, config, root);
  }

  /**
   * @param {string} color
   * @param {GalleryConfig} config
   */
  function resolveGalleryImagesByColor(color, config = window.variantGalleryConfig || {}) {
    if (!color) return null;

    const byColor = window.variantGalleryByColor || {};
    if (Array.isArray(byColor[color]) && byColor[color].length) return byColor[color];

    const normalized = color.toLowerCase().trim();
    const match = Object.keys(byColor).find((key) => key.toLowerCase().trim() === normalized);
    return match && byColor[match]?.length ? byColor[match] : null;
  }

  /**
   * Reserve gallery height to prevent layout jump during swap.
   * @param {Element | null | undefined} mediaGallery
   */
  function reserveGalleryHeight(mediaGallery) {
    if (!(mediaGallery instanceof HTMLElement)) return;
    const height = mediaGallery.offsetHeight;
    if (height > 0) {
      mediaGallery.style.setProperty('--variant-gallery-min-height', `${height}px`);
    }
  }

  /**
   * Show the target variant's first image immediately so shoppers never see a white gap.
   * @param {Element | null | undefined} mediaRoot
   * @param {GalleryImage[]} images
   */
  function showGalleryPreview(mediaRoot, images) {
    if (!(mediaRoot instanceof HTMLElement) || !images?.length) return;

    const mediaGallery = mediaRoot.querySelector('media-gallery');
    reserveGalleryHeight(mediaGallery);

    let preview = mediaRoot.querySelector('.variant-gallery-preview');
    if (!preview) {
      preview = document.createElement('div');
      preview.className = 'variant-gallery-preview';
      preview.setAttribute('aria-hidden', 'true');
      mediaRoot.appendChild(preview);
    }

    let img = preview.querySelector('img');
    if (!(img instanceof HTMLImageElement)) {
      img = document.createElement('img');
      img.className = 'variant-gallery-preview__image';
      img.decoding = 'async';
      preview.appendChild(img);
    }

    const first = images[0];
    const nextSrc = imageUrl(first.src, 1200);
    if (img.src !== nextSrc) {
      img.src = nextSrc;
      img.srcset = buildSrcset(first.src, [352, 832, 1200, 1600]);
      img.sizes = '(min-width: 750px) 50vw, 100vw';
    }
    img.alt = first.alt || '';
  }

  /**
   * @param {Element | null | undefined} mediaRoot
   */
  function hideGalleryPreview(mediaRoot) {
    if (!(mediaRoot instanceof HTMLElement)) return;
    mediaRoot.querySelector('.variant-gallery-preview')?.remove();
  }

  /**
   * Snapshot Horizon's default gallery HTML for fallback when metafield is empty.
   * @param {Element} mediaGallery
   */
  function captureDefaultGallery(mediaGallery) {
    if (!(mediaGallery instanceof HTMLElement)) return;
    if (!mediaGallery._variantGalleryDefaultHtml) {
      mediaGallery._variantGalleryDefaultHtml = mediaGallery.innerHTML;
    }

    const zoomDialog = mediaGallery.querySelector('zoom-dialog');
    if (zoomDialog) captureZoomTemplates(zoomDialog);
  }

  /**
   * @param {Element} zoomDialog
   */
  function captureZoomTemplates(zoomDialog) {
    if (!(zoomDialog instanceof HTMLElement)) return;

    const templateItem = zoomDialog.querySelector('[ref="media"], [ref="media[]"]');
    if (templateItem instanceof HTMLElement && !zoomDialog._variantGalleryMediaTemplate) {
      zoomDialog._variantGalleryMediaTemplate = templateItem.outerHTML;
    }

    const thumbTemplate = zoomDialog.querySelector('.dialog-thumbnails-list button');
    if (thumbTemplate instanceof HTMLElement && !zoomDialog._variantGalleryThumbTemplate) {
      zoomDialog._variantGalleryThumbTemplate = thumbTemplate.outerHTML;
    }
  }

  /**
   * Completely remove previous gallery DOM before rebuilding.
   * Clears hosts with innerHTML so default product.media cannot remain.
   * @param {Element} mediaGallery
   */
  function destroyGallery(mediaGallery) {
    if (!(mediaGallery instanceof HTMLElement)) return;

    console.log('[variant-gallery] Destroying gallery / thumbnails (exclusive clear)...');

    const slideshow = mediaGallery.querySelector('slideshow-component');
    if (slideshow) {
      const host = getSlidesHost(slideshow);
      // Wipe every slide from the scroller — do not append on top of product.media
      host.querySelectorAll('slideshow-slide, .product-media-container').forEach((node) => node.remove());
      // If Horizon left other slide wrappers, clear scroller children that look like slides
      Array.from(host.children).forEach((child) => {
        const tag = child.tagName ? child.tagName.toLowerCase() : '';
        if (tag === 'slideshow-slide' || child.classList.contains('product-media-container')) {
          child.remove();
        }
      });
    }

    mediaGallery.querySelectorAll('slideshow-slide').forEach((slide) => slide.remove());

    mediaGallery
      .querySelectorAll(
        [
          'slideshow-controls[thumbnails] .slideshow-controls__thumbnails',
          'slideshow-controls .slideshow-controls__thumbnails',
          '.slideshow-controls__thumbnails',
          '[ref="thumbnails"]',
          '.dialog-thumbnails-list',
          '.slideshow-controls__dots',
        ].join(', ')
      )
      .forEach((list) => {
        list.innerHTML = '';
      });

    mediaGallery
      .querySelectorAll(
        'slideshow-controls[thumbnails] .slideshow-control, slideshow-controls[thumbnails] button.slideshow-controls__thumbnail'
      )
      .forEach((btn) => btn.remove());

    mediaGallery.querySelectorAll('[aria-selected="true"]').forEach((control) => {
      control.setAttribute('aria-selected', 'false');
    });

    const grid = mediaGallery.querySelector('.media-gallery__grid');
    if (grid) grid.innerHTML = '';

    const zoomDialog = mediaGallery.querySelector('zoom-dialog');
    if (zoomDialog) {
      zoomDialog.querySelectorAll('[ref="media"], [ref="media[]"]').forEach((node) => node.remove());
      const thumbList = zoomDialog.querySelector('.dialog-thumbnails-list');
      if (thumbList) thumbList.innerHTML = '';
    }

    delete mediaGallery.dataset.variantGallerySignature;
  }

  /**
   * Remove any Horizon/default product media that reappeared after our rebuild.
   * Only keeps nodes marked data-variant-gallery-owned.
   * @param {Element} mediaGallery
   * @param {number} expectedCount
   */
  function purgeForeignMedia(mediaGallery, expectedCount) {
    if (!(mediaGallery instanceof HTMLElement)) return;

    mediaGallery.querySelectorAll('slideshow-slide').forEach((slide) => {
      if (slide.getAttribute('data-variant-gallery-owned') !== 'true') {
        slide.remove();
      }
    });

    mediaGallery
      .querySelectorAll(
        'slideshow-controls[thumbnails] .slideshow-control, .slideshow-controls__thumbnail'
      )
      .forEach((btn) => {
        if (btn.getAttribute('data-variant-gallery-owned') !== 'true') {
          btn.remove();
        }
      });

    const slides = mediaGallery.querySelectorAll('slideshow-slide[data-variant-gallery-owned="true"]');
    const thumbs = mediaGallery.querySelectorAll(
      'slideshow-controls[thumbnails] [data-variant-gallery-owned="true"]'
    );

    if (expectedCount > 0 && (slides.length > expectedCount || thumbs.length > expectedCount)) {
      // Trim extras if Horizon duplicated nodes
      Array.from(slides)
        .slice(expectedCount)
        .forEach((node) => node.remove());
      Array.from(thumbs)
        .slice(expectedCount)
        .forEach((node) => node.remove());
    }
  }

  /**
   * Reinitialize Horizon custom elements after DOM rebuild.
   * @param {Element} mediaGallery
   */
  function reinitializeHorizonGallery(mediaGallery) {
    const slideshow = mediaGallery.querySelector('slideshow-component');
    if (slideshow) {
      customElements.upgrade(slideshow);
      if (typeof slideshow.updatedCallback === 'function') {
        try {
          slideshow.updatedCallback();
        } catch {
          /* ignore */
        }
      }
    }

    mediaGallery.querySelectorAll('slideshow-controls, zoom-dialog, drag-zoom-wrapper').forEach((node) => {
      customElements.upgrade(node);
      if (typeof node.updatedCallback === 'function') {
        try {
          node.updatedCallback();
        } catch {
          /* ignore */
        }
      }
    });

    bindGalleryNavigation(mediaGallery);
  }

  /**
   * Sync aria-selected / aria-hidden after a slide change.
   * @param {Element} mediaGallery
   * @param {number} index
   */
  function syncGalleryChrome(mediaGallery, index) {
    const slides = mediaGallery.querySelectorAll(
      'slideshow-slide[data-variant-gallery-owned="true"]'
    );
    slides.forEach((slide, i) => {
      slide.setAttribute('aria-hidden', i === index ? 'false' : 'true');
    });

    mediaGallery
      .querySelectorAll(
        'slideshow-controls[thumbnails] .slideshow-control[data-variant-gallery-owned="true"], .slideshow-controls__thumbnail[data-variant-gallery-owned="true"]'
      )
      .forEach((btn, i) => {
        btn.setAttribute('aria-selected', i === index ? 'true' : 'false');
      });

    mediaGallery.querySelectorAll('.slideshow-controls__dots button').forEach((dot, i) => {
      dot.setAttribute('aria-selected', i === index ? 'true' : 'false');
    });

    mediaGallery.querySelectorAll('.slideshow-controls__counter-current').forEach((el) => {
      el.textContent = String(index + 1);
    });

    const activeThumb = mediaGallery.querySelector(
      `slideshow-controls[thumbnails] .slideshow-control[data-variant-gallery-thumb="${index}"], .slideshow-controls__thumbnail[data-variant-gallery-thumb="${index}"]`
    );
    if (activeThumb instanceof HTMLElement) {
      const scroller =
        activeThumb.closest('.slideshow-controls__thumbnails') ||
        activeThumb.closest('[ref="thumbnails"]');
      if (scroller instanceof HTMLElement) {
        activeThumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      }
    }
  }

  /**
   * Navigate to a slide — works on desktop + mobile after dynamic rebuild.
   * @param {Element} mediaGallery
   * @param {number} index
   * @param {{ animate?: boolean }} [options]
   */
  function selectGallerySlide(mediaGallery, index, options = {}) {
    const { animate = true } = options;
    const slides = Array.from(
      mediaGallery.querySelectorAll('slideshow-slide[data-variant-gallery-owned="true"]')
    );
    if (!slides.length || index < 0 || index >= slides.length) return;

    const slideshow = mediaGallery.querySelector('slideshow-component');

    if (slideshow && typeof slideshow.select === 'function') {
      try {
        slideshow.select(index, undefined, { animate });
      } catch {
        /* stale Horizon refs after rebuild — we scroll manually below */
      }
    }

    const slide = slides[index];
    if (slide instanceof HTMLElement) {
      const scroller =
        (slideshow &&
          (slideshow.querySelector('[ref="scroller"]') ||
            slideshow.querySelector('slideshow-container'))) ||
        null;

      if (scroller instanceof HTMLElement) {
        const left = slide.offsetLeft;
        try {
          scroller.scrollTo({ left, behavior: animate ? 'smooth' : 'auto' });
        } catch {
          scroller.scrollLeft = left;
        }
      } else {
        try {
          slide.scrollIntoView({
            behavior: animate ? 'smooth' : 'instant',
            block: 'nearest',
            inline: 'center',
          });
        } catch {
          slide.scrollIntoView({ block: 'nearest', inline: 'center' });
        }
      }
    }

    syncGalleryChrome(mediaGallery, index);
  }

  /**
   * Wire thumbnail / dot / arrow clicks so desktop navigation works after metafield rebuild.
   * Horizon's on:click="/select/N" often does not rebind on dynamically created nodes.
   * @param {Element} mediaGallery
   */
  function bindGalleryNavigation(mediaGallery) {
    if (!(mediaGallery instanceof HTMLElement)) return;
    if (mediaGallery.dataset.variantGalleryNavBound === 'true') return;

    mediaGallery.dataset.variantGalleryNavBound = 'true';

    mediaGallery.addEventListener(
      'click',
      (event) => {
        if (!(event.target instanceof Element)) return;
        if (mediaGallery.dataset.variantGalleryActive !== 'true') return;

        const thumb = event.target.closest(
          'slideshow-controls[thumbnails] .slideshow-control, .slideshow-controls__thumbnail, .slideshow-controls__dots button'
        );
        if (thumb instanceof HTMLElement) {
          const list =
            thumb.closest('.slideshow-controls__thumbnails') ||
            thumb.closest('.slideshow-controls__dots') ||
            thumb.parentElement;
          const indexAttr = thumb.dataset.variantGalleryThumb;
          let index =
            indexAttr != null && indexAttr !== ''
              ? Number(indexAttr)
              : list
                ? Array.from(list.children).indexOf(thumb)
                : -1;

          if (Number.isFinite(index) && index >= 0) {
            event.preventDefault();
            event.stopPropagation();
            selectGallerySlide(mediaGallery, index, { animate: true });
          }
          return;
        }

        const prev = event.target.closest(
          'slideshow-controls [name="previous"], button[name="previous"], .slideshow-control--previous'
        );
        const next = event.target.closest(
          'slideshow-controls [name="next"], button[name="next"], .slideshow-control--next'
        );
        if (prev || next) {
          const current =
            mediaGallery.querySelector(
              'slideshow-controls[thumbnails] .slideshow-control[aria-selected="true"]'
            ) ||
            mediaGallery.querySelector('slideshow-slide[aria-hidden="false"]');
          let index = 0;
          if (current instanceof HTMLElement && current.dataset.variantGalleryThumb != null) {
            index = Number(current.dataset.variantGalleryThumb) || 0;
          } else if (current) {
            const slides = Array.from(
              mediaGallery.querySelectorAll('slideshow-slide[data-variant-gallery-owned="true"]')
            );
            index = Math.max(0, slides.indexOf(current));
          }
          const total = mediaGallery.querySelectorAll(
            'slideshow-slide[data-variant-gallery-owned="true"]'
          ).length;
          const nextIndex = prev
            ? Math.max(0, index - 1)
            : Math.min(total - 1, index + 1);
          if (nextIndex !== index) {
            event.preventDefault();
            event.stopPropagation();
            selectGallerySlide(mediaGallery, nextIndex, { animate: true });
          }
        }
      },
      true
    );

    // Desktop: hovering thumbnails should also switch (matches Horizon pointerenter)
    mediaGallery.addEventListener(
      'pointerenter',
      (event) => {
        if (!(event.target instanceof Element)) return;
        if (mediaGallery.dataset.variantGalleryActive !== 'true') return;
        // Skip touch — mobile already swipes the main slideshow
        if (event.pointerType === 'touch') return;

        const thumb = event.target.closest(
          'slideshow-controls[thumbnails] .slideshow-control[data-variant-gallery-owned="true"]'
        );
        if (!(thumb instanceof HTMLElement)) return;

        const index = Number(thumb.dataset.variantGalleryThumb);
        if (!Number.isFinite(index) || index < 0) return;
        selectGallerySlide(mediaGallery, index, { animate: false });
      },
      true
    );

    bindGalleryDrag(mediaGallery);
  }

  /**
   * Desktop mouse-drag / pointer swipe for the main image.
   * Horizon's built-in drag stops working after we replace slides (stale refs.slides).
   * @param {Element} mediaGallery
   */
  function bindGalleryDrag(mediaGallery) {
    if (!(mediaGallery instanceof HTMLElement)) return;
    if (mediaGallery.dataset.variantGalleryDragBound === 'true') return;
    mediaGallery.dataset.variantGalleryDragBound = 'true';

    /** @type {{ scroller: HTMLElement, pointerId: number, startX: number, startScroll: number, moved: boolean, dragging: boolean } | null} */
    let drag = null;
    let suppressClickUntil = 0;

    const getScroller = () => {
      const slideshow = mediaGallery.querySelector('slideshow-component');
      if (!(slideshow instanceof HTMLElement)) return null;
      const scroller =
        slideshow.querySelector('[ref="scroller"]') || slideshow.querySelector('slideshow-container');
      return scroller instanceof HTMLElement ? scroller : null;
    };

    const getOwnedSlides = () =>
      Array.from(mediaGallery.querySelectorAll('slideshow-slide[data-variant-gallery-owned="true"]'));

    const indexFromScroll = (scroller) => {
      const slides = getOwnedSlides();
      if (!slides.length) return 0;
      const center = scroller.scrollLeft + scroller.clientWidth / 2;
      let best = 0;
      let bestDist = Infinity;
      slides.forEach((slide, i) => {
        if (!(slide instanceof HTMLElement)) return;
        const mid = slide.offsetLeft + slide.offsetWidth / 2;
        const dist = Math.abs(mid - center);
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      });
      return best;
    };

    mediaGallery.addEventListener(
      'pointerdown',
      (event) => {
        if (mediaGallery.dataset.variantGalleryActive !== 'true') return;
        if (!(event.target instanceof Element)) return;
        // Thumbnails / controls have their own handlers
        if (event.target.closest('slideshow-controls, zoom-dialog, button, a, input, label')) return;
        if (!event.target.closest('slideshow-component, slideshow-slide, .product-media-container')) return;

        const slides = getOwnedSlides();
        if (slides.length <= 1) return;

        const scroller = getScroller();
        if (!scroller) return;

        drag = {
          scroller,
          pointerId: event.pointerId,
          startX: event.clientX,
          startScroll: scroller.scrollLeft,
          moved: false,
          dragging: false,
        };
      },
      true
    );

    mediaGallery.addEventListener(
      'pointermove',
      (event) => {
        if (!drag || event.pointerId !== drag.pointerId) return;

        const dx = event.clientX - drag.startX;
        if (!drag.dragging) {
          if (Math.abs(dx) < 8) return;
          drag.dragging = true;
          drag.moved = true;
          drag.scroller.classList.add('is-variant-gallery-dragging');
          drag.scroller.style.scrollSnapType = 'none';
          drag.scroller.style.cursor = 'grabbing';
          try {
            drag.scroller.setPointerCapture(event.pointerId);
          } catch {
            /* ignore */
          }
          mediaGallery.querySelector('slideshow-component')?.setAttribute('dragging', '');
        }

        event.preventDefault();
        drag.scroller.scrollLeft = drag.startScroll - dx;
      },
      true
    );

    const endDrag = (event) => {
      if (!drag || (event && event.pointerId !== drag.pointerId)) return;

      const { scroller, moved, dragging } = drag;
      drag = null;

      scroller.classList.remove('is-variant-gallery-dragging');
      scroller.style.cursor = '';
      scroller.style.removeProperty('scroll-snap-type');
      mediaGallery.querySelector('slideshow-component')?.removeAttribute('dragging');

      if (!dragging && !moved) return;

      suppressClickUntil = Date.now() + 400;
      const index = indexFromScroll(scroller);
      selectGallerySlide(mediaGallery, index, { animate: true });
    };

    mediaGallery.addEventListener('pointerup', endDrag, true);
    mediaGallery.addEventListener('pointercancel', endDrag, true);

    // After a drag, block the click so zoom does not open accidentally
    mediaGallery.addEventListener(
      'click',
      (event) => {
        if (Date.now() > suppressClickUntil) return;
        if (!(event.target instanceof Element)) return;
        if (!event.target.closest('slideshow-slide, .product-media-container')) return;
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );

    // Keep thumbs in sync when user scrolls/swipes (touch or trackpad)
    let scrollTimer = null;
    mediaGallery.addEventListener(
      'scroll',
      (event) => {
        if (mediaGallery.dataset.variantGalleryActive !== 'true') return;
        if (!(event.target instanceof Element)) return;
        const scroller = getScroller();
        if (!scroller || event.target !== scroller) return;
        if (drag?.dragging) return;

        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(() => {
          const index = indexFromScroll(scroller);
          syncGalleryChrome(mediaGallery, index);
        }, 80);
      },
      true
    );
  }

  function escapeHtml(text) {
    return String(text || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /**
   * @param {Element} slideshow
   */
  function getSlidesHost(slideshow) {
    return (
      slideshow.querySelector('[ref="scroller"]') ||
      slideshow.querySelector('slideshow-container') ||
      slideshow
    );
  }

  /**
   * @param {GalleryImage} image
   * @param {number} index
   * @param {string | null} zoomDialogId
   */
  function buildSlideElement(image, index, zoomDialogId) {
    const slide = document.createElement('slideshow-slide');
    slide.setAttribute('slide-id', `variant-gallery-${index}`);
    slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
    slide.setAttribute('data-variant-gallery-owned', 'true');
    slide.removeAttribute('hidden');

    const container = document.createElement('div');
    container.className =
      'product-media-container product-media-container--image product-media-container--zoomable media-fit-contain';
    container.setAttribute('data-media-type', 'image');
    container.setAttribute('data-variant-gallery-owned', 'true');
    if (zoomDialogId) {
      container.setAttribute('on:click', `${zoomDialogId}/open/${index}`);
    }

    const media = document.createElement('div');
    media.className = 'product-media media-fit-contain';

    const img = document.createElement('img');
    img.className = 'product-media__image';
    applyImageToElement(img, image, index === 0);

    media.appendChild(img);
    container.appendChild(media);
    slide.appendChild(container);
    return slide;
  }

  /**
   * @param {GalleryImage} image
   * @param {number} index
   * @param {number} total
   */
  function buildThumbButton(image, index, total) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'slideshow-control slideshow-controls__thumbnail';
    button.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
    button.setAttribute('aria-label', `${index + 1} / ${total}`);
    button.setAttribute('on:click', `/select/${index}`);
    button.setAttribute('on:pointerenter', `/select/${index}`);
    button.setAttribute('on:focus', `/select/${index}`);
    button.setAttribute('data-variant-gallery-owned', 'true');
    button.dataset.variantGalleryThumb = String(index);
    button.dataset.variantGallerySrc = image.src;

    const img = document.createElement('img');
    img.src = imageUrl(image.src, THUMB_WIDTH);
    img.alt = image.alt || '';
    img.loading = index === 0 ? 'eager' : 'lazy';
    img.decoding = 'async';
    button.appendChild(img);
    return button;
  }

  /**
   * @param {Element} mediaGallery
   * @param {GalleryImage[]} images
   * @param {Object} [options]
   * @param {boolean} [options.animate]
   */
  async function swapGallery(mediaGallery, images, options = {}) {
    const { animate = false, force = false } = options;
    if (!(mediaGallery instanceof HTMLElement)) return;
    if (isSwapping && !force) return;

    if (!mediaGallery._variantGallerySwapGen) mediaGallery._variantGallerySwapGen = 0;
    const swapGeneration = ++mediaGallery._variantGallerySwapGen;

    const mediaRoot = mediaGallery.closest('[data-variant-gallery-media]');
    const signature = gallerySignature(images);

    if (
      !force &&
      mediaGallery.dataset.variantGalleryActive === 'true' &&
      mediaGallery.dataset.variantGallerySignature === signature
    ) {
      const slideCount = mediaGallery.querySelectorAll('slideshow-slide').length;
      const thumbCount = mediaGallery.querySelectorAll(
        'slideshow-controls[thumbnails] .slideshow-control, .slideshow-controls__thumbnail'
      ).length;
      if (slideCount === images.length && thumbCount === images.length) {
        hideGalleryPreview(mediaRoot);
        return;
      }
    }

    isSwapping = true;
    ignoreMutationsUntil = Date.now() + 800;

    try {
      reserveGalleryHeight(mediaGallery);
      destroyGallery(mediaGallery);
      await preloadGalleryImages(images);

      if (swapGeneration !== mediaGallery._variantGallerySwapGen) return;

      mediaGallery.dataset.variantGalleryActive = 'true';
      mediaGallery.dataset.variantGallerySignature = signature;

      const zoomDialog = mediaGallery.querySelector('zoom-dialog');
      const zoomDialogId = zoomDialog?.id ? `#${zoomDialog.id}` : null;

      rebuildSlideshow(mediaGallery, images, zoomDialogId);
      rebuildThumbnails(mediaGallery, images);
      rebuildGrid(mediaGallery, images, zoomDialogId);
      updateZoomDialog(zoomDialog, images);
      updatePagination(mediaGallery, images.length);
      purgeForeignMedia(mediaGallery, images.length);
      reinitializeHorizonGallery(mediaGallery);
      bindGalleryNavigation(mediaGallery);

      if (swapGeneration !== mediaGallery._variantGallerySwapGen) return;

      await waitForGalleryPaint();
      purgeForeignMedia(mediaGallery, images.length);
      selectFirstSlide(mediaGallery);
      ensureSlideshowScrollable(mediaGallery);

      // After Horizon morph: purge default product.media that got re-injected (never merge)
      setTimeout(() => {
        if (swapGeneration !== mediaGallery._variantGallerySwapGen) return;
        if (mediaGallery.dataset.variantGallerySignature !== signature) return;

        ignoreMutationsUntil = Date.now() + 400;
        purgeForeignMedia(mediaGallery, images.length);

        const slideCount = mediaGallery.querySelectorAll(
          'slideshow-slide[data-variant-gallery-owned="true"]'
        ).length;
        const thumbCount = mediaGallery.querySelectorAll(
          'slideshow-controls[thumbnails] [data-variant-gallery-owned="true"]'
        ).length;

        if (slideCount !== images.length || thumbCount !== images.length) {
          console.log('[variant-gallery] Counts wrong after morph — exclusive rebuild', {
            slideCount,
            thumbCount,
            expected: images.length,
          });
          rebuildSlideshow(mediaGallery, images, zoomDialogId);
          rebuildThumbnails(mediaGallery, images);
          purgeForeignMedia(mediaGallery, images.length);
          reinitializeHorizonGallery(mediaGallery);
          selectFirstSlide(mediaGallery);
          ensureSlideshowScrollable(mediaGallery);
        } else {
          ensureSlideshowScrollable(mediaGallery);
          scrollActiveThumbnailIntoView(mediaGallery);
        }
      }, 350);

      if (animate) {
        mediaGallery.classList.add('is-variant-gallery-fading', 'is-variant-gallery-fade-in');
        await delay(250);
        mediaGallery.classList.remove('is-variant-gallery-fading', 'is-variant-gallery-fade-in');
      }

      hideGalleryPreview(mediaRoot);
      console.log('[variant-gallery] Gallery rebuilt:', images.length, 'images');

      mediaGallery.closest('[data-variant-gallery-media]')?.dispatchEvent(
        new CustomEvent('variant-gallery:updated', {
          bubbles: true,
          detail: { imageCount: images.length, signature },
        })
      );
    } finally {
      isSwapping = false;
      ignoreMutationsUntil = Date.now() + 400;
      if (swapGeneration === mediaGallery._variantGallerySwapGen) {
        hideGalleryPreview(mediaRoot);
      }
    }
  }

  /**
   * Ensure the main slideshow can scroll between metafield slides on desktop + mobile.
   * @param {Element} mediaGallery
   */
  function ensureSlideshowScrollable(mediaGallery) {
    const slideshow = mediaGallery.querySelector('slideshow-component');
    if (!(slideshow instanceof HTMLElement)) return;

    const slideCount = mediaGallery.querySelectorAll(
      'slideshow-slide[data-variant-gallery-owned="true"]'
    ).length;

    slideshow.classList.toggle('slideshow--single-media', slideCount <= 1);
    slideshow.classList.toggle('product-media-gallery__slideshow--single-media', slideCount <= 1);
    slideshow.removeAttribute('disabled');
    slideshow.removeAttribute('dragging');
    if (slideCount > 1) {
      slideshow.setAttribute('aria-roledescription', 'carousel');
    }

    const scroller =
      slideshow.querySelector('[ref="scroller"]') || slideshow.querySelector('slideshow-container');
    if (scroller instanceof HTMLElement) {
      scroller.style.display = 'flex';
      scroller.style.flexDirection = 'row';
      scroller.style.flexWrap = 'nowrap';
      scroller.style.width = '100%';
      if (slideCount > 1) {
        scroller.style.overflowX = 'auto';
        scroller.style.overflowY = 'hidden';
        scroller.style.scrollSnapType = 'x mandatory';
        scroller.style.webkitOverflowScrolling = 'touch';
        scroller.style.cursor = 'grab';
        scroller.style.touchAction = 'pan-x';
      } else {
        scroller.style.overflowX = 'hidden';
        scroller.style.cursor = '';
        scroller.style.touchAction = '';
      }
    }

    mediaGallery
      .querySelectorAll('slideshow-slide[data-variant-gallery-owned="true"]')
      .forEach((slide) => {
        if (slide instanceof HTMLElement) {
          slide.removeAttribute('hidden');
          slide.style.scrollSnapAlign = 'center';
          slide.style.scrollSnapStop = 'always';
          slide.style.flex = '0 0 100%';
          slide.style.minWidth = '100%';
          slide.style.maxWidth = '100%';
          slide.style.width = '100%';
          slide.style.boxSizing = 'border-box';
          // Keep inactive slides in the scroll track (Horizon sometimes hides them)
          slide.style.visibility = 'visible';
          slide.style.contentVisibility = 'visible';
        }
      });

    // Thumbnail strip must scroll when many thumbs (or left-rail overflow)
    mediaGallery.querySelectorAll('.slideshow-controls__thumbnails, [ref="thumbnails"]').forEach((list) => {
      if (!(list instanceof HTMLElement)) return;
      list.style.overflowX = 'auto';
      list.style.overflowY = 'auto';
      list.style.webkitOverflowScrolling = 'touch';
      list.style.scrollSnapType = 'both proximity';
    });

    bindGalleryDrag(mediaGallery);
  }

  /**
   * @param {Element} mediaGallery
   * @param {GalleryImage[]} images
   * @param {string | null} zoomDialogId
   */
  function rebuildSlideshow(mediaGallery, images, zoomDialogId) {
    const slideshow = mediaGallery.querySelector('slideshow-component');
    if (!slideshow) return;

    const host = getSlidesHost(slideshow);
    // Exclusive rebuild: clear host slides first, then add ONLY metafield images
    host.querySelectorAll('slideshow-slide, .product-media-container').forEach((node) => node.remove());
    mediaGallery.querySelectorAll('slideshow-slide').forEach((slide) => slide.remove());

    images.forEach((image, index) => {
      host.appendChild(buildSlideElement(image, index, zoomDialogId));
    });

    slideshow.setAttribute('initial-slide', '0');
    if (images.length <= 1) {
      slideshow.classList.add('slideshow--single-media', 'product-media-gallery__slideshow--single-media');
    } else {
      slideshow.classList.remove('slideshow--single-media', 'product-media-gallery__slideshow--single-media');
    }

    ensureSlideshowScrollable(mediaGallery);
  }

  /**
   * @param {Element} mediaGallery
   * @param {GalleryImage[]} images
   */
  function rebuildThumbnails(mediaGallery, images) {
    console.log('[variant-gallery] Rebuilding Thumbnail Slider...');
    console.log('[variant-gallery] Thumbnail Count:', images.length);
    console.log('[variant-gallery] Gallery Images:', images);

    const lists = Array.from(
      mediaGallery.querySelectorAll(
        [
          'slideshow-controls[thumbnails] .slideshow-controls__thumbnails',
          'slideshow-controls .slideshow-controls__thumbnails',
          '.slideshow-controls__thumbnails',
          '[ref="thumbnails"]',
        ].join(', ')
      )
    );

    // Fallback: create/find thumbnails host on any thumbnails control
    if (!lists.length) {
      mediaGallery.querySelectorAll('slideshow-controls[thumbnails]').forEach((control) => {
        let list = control.querySelector('.slideshow-controls__thumbnails');
        if (!list) {
          list = document.createElement('div');
          list.className = 'slideshow-controls__thumbnails';
          control.appendChild(list);
        }
        lists.push(list);
      });
    }

    lists.forEach((list) => {
      list.innerHTML = '';
      images.forEach((image, index) => {
        list.appendChild(buildThumbButton(image, index, images.length));
      });

      // Reset scroll so first active thumb is visible
      if (list instanceof HTMLElement) {
        list.scrollLeft = 0;
        list.scrollTop = 0;
      }

      // Re-init Horizon controls that own this list
      const controls = list.closest('slideshow-controls');
      if (controls) {
        customElements.upgrade(controls);
        if (typeof controls.updatedCallback === 'function') {
          try {
            controls.updatedCallback();
          } catch {
            /* ignore */
          }
        }
      }
    });

    scrollActiveThumbnailIntoView(mediaGallery);
  }

  /**
   * Ensure the active (first) thumbnail is visible in the strip.
   * @param {Element} mediaGallery
   */
  function scrollActiveThumbnailIntoView(mediaGallery) {
    const active =
      mediaGallery.querySelector(
        'slideshow-controls[thumbnails] .slideshow-control[aria-selected="true"], .slideshow-controls__thumbnail[aria-selected="true"]'
      ) ||
      mediaGallery.querySelector('slideshow-controls[thumbnails] .slideshow-control, .slideshow-controls__thumbnail');

    if (!(active instanceof HTMLElement)) return;

    const scroller =
      active.closest('.slideshow-controls__thumbnails') ||
      active.closest('[ref="thumbnails"]') ||
      active.parentElement;

    if (scroller instanceof HTMLElement && typeof active.scrollIntoView === 'function') {
      try {
        active.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
      } catch {
        scroller.scrollLeft = 0;
      }
    }
  }

  /**
   * @param {Element} mediaGallery
   * @param {GalleryImage[]} images
   * @param {string | null} zoomDialogId
   */
  function rebuildGrid(mediaGallery, images, zoomDialogId) {
    const grid = mediaGallery.querySelector('.media-gallery__grid');
    if (!grid) return;

    grid.innerHTML = '';
    images.forEach((image, index) => {
      const slide = buildSlideElement(image, index, zoomDialogId);
      const container = slide.querySelector('.product-media-container');
      if (container) grid.appendChild(container);
    });

    grid.style.display = 'none';
  }

  /**
   * @param {Element | null} zoomDialog
   * @param {GalleryImage[]} images
   */
  function updateZoomDialog(zoomDialog, images) {
    if (!zoomDialog) return;

    captureZoomTemplates(zoomDialog);
    const templateHtml = zoomDialog._variantGalleryMediaTemplate;
    if (!templateHtml) return;

    const parent =
      zoomDialog.querySelector('[ref="dialog"]') ||
      zoomDialog.querySelector('.dialog-zoomed-gallery') ||
      zoomDialog;

    parent.querySelectorAll('[ref="media"], [ref="media[]"]').forEach((node) => node.remove());

    images.forEach((image, index) => {
      const wrapper = document.createElement('div');
      wrapper.innerHTML = templateHtml;
      const item = wrapper.firstElementChild;
      if (!(item instanceof HTMLElement)) return;

      const img = item.querySelector('img');
      if (img instanceof HTMLImageElement) {
        applyImageToElement(img, image, index === 0);
      }

      parent.appendChild(item);
    });

    const thumbList = zoomDialog.querySelector('.dialog-thumbnails-list');
    const thumbTemplateHtml = zoomDialog._variantGalleryThumbTemplate;
    if (thumbList && thumbTemplateHtml) {
      thumbList.innerHTML = '';
      images.forEach((image, index) => {
        const wrapper = document.createElement('div');
        wrapper.innerHTML = thumbTemplateHtml;
        const btn = wrapper.firstElementChild;
        if (!(btn instanceof HTMLElement)) return;
        btn.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
        const thumbImg = btn.querySelector('img');
        if (thumbImg instanceof HTMLImageElement) {
          thumbImg.src = imageUrl(image.src, THUMB_WIDTH);
          thumbImg.alt = image.alt || '';
        }
        thumbList.appendChild(btn);
      });
    }
  }

  /**
   * @param {Element} mediaGallery
   * @param {number} count
   */
  function updatePagination(mediaGallery, count) {
    const counters = mediaGallery.querySelectorAll('.slideshow-controls__counter');
    counters.forEach((counter) => {
      const current = counter.querySelector('.slideshow-controls__counter-current');
      const total = counter.querySelector('.slideshow-controls__counter-total');
      if (current) current.textContent = '1';
      if (total) total.textContent = String(count);
      else if (counter.childNodes.length) {
        counter.textContent = `1 / ${count}`;
      }
    });

    const dotLists = mediaGallery.querySelectorAll('.slideshow-controls__dots');
    dotLists.forEach((list) => {
      const template = list.querySelector('button');
      if (!(template instanceof HTMLElement)) return;
      list.innerHTML = '';
      for (let i = 0; i < count; i += 1) {
        const dot = template.cloneNode(true);
        if (dot instanceof HTMLElement) {
          dot.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
          dot.setAttribute('aria-label', `${i + 1} / ${count}`);
          dot.setAttribute('on:click', `/select/${i}`);
        }
        list.appendChild(dot);
      }
    });
  }

  /**
   * @param {Element} mediaGallery
   */
  function selectFirstSlide(mediaGallery) {
    bindGalleryNavigation(mediaGallery);
    selectGallerySlide(mediaGallery, 0, { animate: false });
    setTimeout(() => selectGallerySlide(mediaGallery, 0, { animate: false }), 50);
    setTimeout(() => scrollActiveThumbnailIntoView(mediaGallery), 200);
  }

  /**
   * @param {Element} mediaGallery
   */
  function restoreDefaultGallery(mediaGallery) {
    if (!(mediaGallery instanceof HTMLElement)) return;

    console.log('[variant-gallery] No metafield images — restoring default Shopify gallery');
    hideGalleryPreview(mediaGallery.closest('[data-variant-gallery-media]'));
    destroyGallery(mediaGallery);

    if (mediaGallery._variantGalleryDefaultHtml) {
      mediaGallery.innerHTML = mediaGallery._variantGalleryDefaultHtml;
      reinitializeHorizonGallery(mediaGallery);
    }

    mediaGallery.classList.remove(
      'is-variant-gallery-loading',
      'is-variant-gallery-fading',
      'is-variant-gallery-fade-out',
      'is-variant-gallery-fade-in'
    );
    delete mediaGallery.dataset.variantGalleryActive;
    delete mediaGallery.dataset.variantGallerySignature;
    mediaGallery.style.removeProperty('--variant-gallery-min-height');
  }

  class VariantGallerySection {
    /**
     * @param {HTMLElement} root
     * @param {GalleryConfig} config
     */
    constructor(root, config) {
      this.root = root;
      this.config = config;
      this.pending = false;
      this.applyGeneration = 0;
      this.persistentObserver = null;
      this.guardTimer = null;
      this.onPickerPreview = this.onPickerPreview.bind(this);
      this.onVariantInputChange = this.onVariantInputChange.bind(this);
      this.onProductSelect = this.onProductSelect.bind(this);
      this.onPopState = this.onPopState.bind(this);
    }

    mount() {
      console.log('[variant-gallery] Mount start', {
        hasData: hasGalleryData(),
        colors: Object.keys(window.variantGalleryByColor || {}),
        variants: Object.keys(window.variantGallery || {}),
      });

      if (!hasGalleryData()) {
        console.warn(
          '[variant-gallery] No gallery JSON found. Enable storefront access for custom.variant_gallery and add images on variants.'
        );
        return;
      }

      console.log('[variant-gallery] Mounted', this.config);

      document.addEventListener(PRODUCT_SELECT_EVENT, this.onProductSelect, true);
      window.addEventListener('popstate', this.onPopState);

      const picker = findVariantPicker(this.root) || document.querySelector('variant-picker, variant-main-picker');
      if (picker) {
        picker.addEventListener('pointerdown', this.onPickerPreview, true);
        picker.addEventListener('change', this.onVariantInputChange, true);
        picker.addEventListener('click', this.onVariantInputChange, true);
      }

      // Capture-phase document listener — works even if picker markup differs
      document.addEventListener('change', this.onVariantInputChange, true);

      const mediaRoot = this.root.querySelector('[data-variant-gallery-media]');
      if (mediaRoot) {
        this.persistentObserver = new MutationObserver(() => {
          if (isSwapping || Date.now() < ignoreMutationsUntil) return;
          clearTimeout(this.guardTimer);
          this.guardTimer = setTimeout(() => this.enforceGallery(true), 200);
        });
        this.persistentObserver.observe(mediaRoot, {
          childList: true,
          subtree: true,
        });
      }

      this.applyWhenReady(this.config.initialVariantId || getCurrentVariantId(this.root), null, 0);
    }

    /**
     * Retry until media-gallery exists (script may load before gallery HTML).
     */
    applyWhenReady(variantId, resource, attempt) {
      const mediaGallery = findMediaGallery(this.root);
      if (!mediaGallery) {
        if (attempt < 40) {
          setTimeout(() => this.applyWhenReady(variantId, resource, attempt + 1), 100);
        } else {
          console.warn('[variant-gallery] media-gallery not found');
        }
        return;
      }

      captureDefaultGallery(mediaGallery);
      this.applyForVariant(variantId || getCurrentVariantId(this.root), resource, mediaGallery, {
        animate: attempt === 0,
        force: true,
      });
    }

    onVariantInputChange(event) {
      // Ignore unrelated form fields
      if (event && event.target instanceof Element) {
        const relevant = event.target.closest(
          'variant-picker, variant-main-picker, swatches-variant-picker-component, [name="id"], fieldset, .swatch'
        );
        if (!relevant && event.type === 'change') {
          const name = event.target.getAttribute('name') || '';
          if (name !== 'id' && !name.includes('option')) return;
        }
      }

      clearTimeout(this._changeTimer);
      this._changeTimer = setTimeout(() => {
        const variantId = getCurrentVariantId(this.root);
        console.log('[variant-gallery] Variant input/change → apply', variantId);
        this.scheduleApply(variantId, null, 0);
      }, 30);
    }

    scheduleApply(variantId, resource, delayMs = 0) {
      const generation = ++this.applyGeneration;

      setTimeout(async () => {
        if (generation !== this.applyGeneration) return;

        const id = variantId || getCurrentVariantId(this.root);
        const mediaGallery = findMediaGallery(this.root);
        if (!mediaGallery) {
          this.applyWhenReady(id, resource, 0);
          return;
        }

        const images = resolveGalleryImages(id, resource, this.config, this.root);
        console.log('[variant-gallery] scheduleApply', { id, imageCount: images?.length || 0 });

        await this.applyForVariant(id, resource, mediaGallery, { instant: true, force: true });
      }, delayMs);
    }

    /**
     * Show the clicked color's image immediately — no white gap while Horizon updates.
     * @param {Event} event
     */
    onPickerPreview(event) {
      if (!hasGalleryData()) return;

      const mediaRoot = this.root.querySelector('[data-variant-gallery-media]');
      const color = getColorFromPickerInteraction(event, this.config, this.root);
      const images = resolveGalleryImagesByColor(color, this.config);

      if (!images?.length) return;

      showGalleryPreview(mediaRoot, images);
      preloadGalleryImages(images);
    }

    enforceGallery(silent = true) {
      if (isSwapping || Date.now() < ignoreMutationsUntil) return;

      const variantId = getCurrentVariantId(this.root);
      const images = resolveGalleryImages(variantId, null, this.config, this.root);
      const mediaGallery = findMediaGallery(this.root);
      if (!mediaGallery) return;

      if (!images?.length) {
        if (mediaGallery.dataset.variantGalleryActive === 'true') {
          restoreDefaultGallery(mediaGallery);
        }
        return;
      }

      const signature = gallerySignature(images);
      const allSlides = mediaGallery.querySelectorAll('slideshow-slide');
      const ownedSlides = mediaGallery.querySelectorAll('slideshow-slide[data-variant-gallery-owned="true"]');
      const allThumbs = mediaGallery.querySelectorAll(
        'slideshow-controls[thumbnails] .slideshow-control, .slideshow-controls__thumbnail'
      );
      const ownedThumbs = mediaGallery.querySelectorAll(
        'slideshow-controls[thumbnails] [data-variant-gallery-owned="true"]'
      );

      // Default product.media sneaked back in — purge without full flicker when possible
      if (
        mediaGallery.dataset.variantGallerySignature === signature &&
        (allSlides.length > images.length || allThumbs.length > images.length)
      ) {
        console.log('[variant-gallery] Purging merged default product media');
        ignoreMutationsUntil = Date.now() + 400;
        purgeForeignMedia(mediaGallery, images.length);
        if (
          mediaGallery.querySelectorAll('slideshow-slide[data-variant-gallery-owned="true"]').length !==
            images.length ||
          mediaGallery.querySelectorAll(
            'slideshow-controls[thumbnails] [data-variant-gallery-owned="true"]'
          ).length !== images.length
        ) {
          this.applyForVariant(variantId, null, mediaGallery, { instant: silent, force: true });
        }
        return;
      }

      if (
        mediaGallery.dataset.variantGallerySignature === signature &&
        ownedSlides.length === images.length &&
        ownedThumbs.length === images.length &&
        allSlides.length === images.length &&
        allThumbs.length === images.length
      ) {
        return;
      }

      console.log('[variant-gallery] Enforcing exclusive gallery rebuild', {
        allSlides: allSlides.length,
        ownedSlides: ownedSlides.length,
        allThumbs: allThumbs.length,
        ownedThumbs: ownedThumbs.length,
        expected: images.length,
      });
      this.applyForVariant(variantId, null, mediaGallery, { instant: silent, force: true });
    }

    destroy() {
      document.removeEventListener(PRODUCT_SELECT_EVENT, this.onProductSelect, true);
      document.removeEventListener('change', this.onVariantInputChange, true);
      window.removeEventListener('popstate', this.onPopState);
      this.persistentObserver?.disconnect();
      clearTimeout(this.guardTimer);
      clearTimeout(this._changeTimer);

      const picker = findVariantPicker(this.root);
      picker?.removeEventListener('pointerdown', this.onPickerPreview, true);
      picker?.removeEventListener('change', this.onVariantInputChange, true);
      picker?.removeEventListener('click', this.onVariantInputChange, true);
    }

    /**
     * @param {Event} event
     */
    async onProductSelect(event) {
      if (!(event.target instanceof Element)) return;
      if (!this.isEventForThisSection(event.target)) return;

      const productSelect = /** @type {Object} */ (event);
      const mediaRoot = this.root.querySelector('[data-variant-gallery-media]');
      const previousGallery = findMediaGallery(this.root);
      const generation = ++this.applyGeneration;
      ignoreMutationsUntil = Date.now() + 600;

      try {
        let resource = null;
        let variantId = getCurrentVariantId(this.root);

        // Horizon may expose a promise — use it when present, but never require it
        if (productSelect.promise && typeof productSelect.promise.then === 'function') {
          try {
            const result = await productSelect.promise;
            resource = result?.detail?.resource ?? result?.variant ?? null;
            variantId = resource?.id ?? getCurrentVariantId(this.root);
          } catch (err) {
            if (err?.name === 'AbortError') return;
          }
        } else {
          resource = productSelect.detail?.resource ?? productSelect.detail?.variant ?? null;
          variantId = resource?.id ?? getCurrentVariantId(this.root);
        }

        if (generation !== this.applyGeneration) return;

        const images = resolveGalleryImages(variantId, resource, this.config, this.root);
        console.log('[variant-gallery] product:select → apply', { variantId, count: images?.length || 0 });

        if (!images?.length) {
          hideGalleryPreview(mediaRoot);
          restoreDefaultGallery(findMediaGallery(this.root));
          return;
        }

        showGalleryPreview(mediaRoot, images);

        // Short wait then force exclusive rebuild
        await waitForHorizonGalleryUpdate(this.root, previousGallery, 250);
        if (generation !== this.applyGeneration) return;

        await this.applyForVariant(variantId, resource, findMediaGallery(this.root), {
          instant: true,
          force: true,
        });
      } catch (error) {
        if (error?.name !== 'AbortError') {
          console.warn('[variant-gallery] product select handler failed:', error);
        }
        // Fallback: still try to apply from current form state
        this.scheduleApply(getCurrentVariantId(this.root), null, 50);
        hideGalleryPreview(mediaRoot);
      }
    }

    /**
     * @param {Element} target
     */
    isEventForThisSection(target) {
      if (this.root.contains(target)) return true;

      const picker = target.closest(
        'variant-main-picker, variant-picker, swatches-variant-picker-component'
      );
      if (!picker) {
        // Still allow document-level product select for this product page
        return Boolean(document.querySelector(`[data-variant-gallery-section="${this.config.sectionId}"]`));
      }

      const pickerProductId = picker.dataset?.productId;
      if (!pickerProductId) return true;
      if (String(pickerProductId) === String(this.config.productId)) return true;

      return Boolean(picker.closest(`#shopify-section-${this.config.sectionId}`));
    }

    onPopState() {
      const variantId = getCurrentVariantId(this.root);
      if (!variantId) return;
      this.scheduleApply(variantId, null, 0);
    }

    /**
     * @param {string | number | null | undefined} variantId
     * @param {Record<string, unknown> | null | undefined} resource
     * @param {Element | null | undefined} [galleryOverride]
     * @param {Object} [options]
     * @param {boolean} [options.instant]
     * @param {boolean} [options.animate]
     * @param {boolean} [options.force]
     */
    async applyForVariant(variantId, resource, galleryOverride, options = {}) {
      const { instant = false, animate = false, force = false } = options;
      const mediaGallery = galleryOverride || findMediaGallery(this.root);

      try {
        if (!mediaGallery) return;

        const resolvedId = variantId || getCurrentVariantId(this.root);
        const images = resolveGalleryImages(resolvedId, resource, this.config, this.root);

        if (!images?.length) {
          restoreDefaultGallery(mediaGallery);
          return;
        }

        await swapGallery(mediaGallery, images, { animate: animate && !instant, force });
      } catch (error) {
        console.warn('[variant-gallery] apply failed:', error);
        hideGalleryPreview(mediaGallery.closest('[data-variant-gallery-media]'));
      }
    }
  }

  /**
   * @returns {string | null}
   */
  function getVariantIdFromUrl() {
    return new URL(window.location.href).searchParams.get('variant');
  }

  /**
   * Load gallery JSON for a specific section bootstrap node.
   * @param {HTMLElement} node
   */
  function loadSectionGalleryData(node) {
    const sectionId = node.dataset.variantGallerySection;
    if (!sectionId) return null;

    const jsonNode = document.getElementById(`variant-gallery-json-${sectionId}`);
    if (!jsonNode) return null;

    try {
      const data = JSON.parse(jsonNode.textContent || '{}');
      window.variantGallery = { ...(window.variantGallery || {}), ...(data.variantGallery || {}) };
      window.variantGalleryByColor = {
        ...(window.variantGalleryByColor || {}),
        ...(data.variantGalleryByColor || {}),
      };
      return data.config || null;
    } catch (error) {
      console.warn('[variant-gallery] Failed to parse gallery JSON:', error);
      return null;
    }
  }

  /**
   * Initialize controllers once per product section.
   */
  function init() {
    document.querySelectorAll('[data-variant-gallery-section]').forEach((node) => {
      if (!(node instanceof HTMLElement)) return;

      const sectionId = node.dataset.variantGallerySection;
      if (!sectionId || sections.has(sectionId)) return;

      const config = loadSectionGalleryData(node);
      if (!config || String(config.sectionId) !== String(sectionId)) return;

      const galleryCount = Number(node.dataset.variantGalleryCount || 0);
      console.log('[variant-gallery] Bootstrap:', {
        sectionId,
        galleryCount,
        colors: Object.keys(window.variantGalleryByColor || {}),
        variantIds: Object.keys(window.variantGallery || {}),
      });

      if (galleryCount === 0) {
        console.warn(
          '[variant-gallery] No variant_gallery metafield images found. Add images to variants and enable storefront access for custom.variant_gallery.'
        );
      }

      window.variantGalleryConfig = config;

      const sectionRoot =
        document.getElementById(`shopify-section-${sectionId}`) ||
        node.closest('.shopify-section') ||
        node.closest('.product-information') ||
        node.parentElement;

      if (!(sectionRoot instanceof HTMLElement)) return;

      const controller = new VariantGallerySection(sectionRoot, config);
      controller.mount();
      sections.set(sectionId, controller);
    });
  }

  function bootstrap() {
    if (document.querySelector('[data-variant-gallery-section]')) {
      init();
      return;
    }

    if (!bootstrapped) {
      bootstrapped = true;
      const observer = new MutationObserver(() => {
        if (document.querySelector('[data-variant-gallery-section]')) {
          observer.disconnect();
          init();
        }
      });
      observer.observe(document.documentElement, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
})();
